import React from 'react'
import { IconBriefcase, IconHome, IconSparkles, IconUsers } from '@tabler/icons-react'
import SectionTitle from './shared/SectionTitle'
import PremiumCard from './shared/PremiumCard'

export default function ScenariosSection() {
  const scenarios = [
    {
      icon: IconHome,
      title: '房东 / 长租公寓 / 租赁公司',
      problems: ['人看来挺正常，结果住进去后各种拖欠、赖账……', '门店凭感觉审批，标准不统一，出了事没人说得清'],
      solutions: ['签约前一键生成租客风险报告', '提示法律纠纷、偿付压力等关键信号', '用简单的等级与标签提示风险程度'],
      colorScheme: 'blue',
      size: 'large',
    },
    {
      icon: IconSparkles,
      title: '家政 / 到家服务平台 / 家政公司',
      problems: ['上门服务出了问题，客户找平台，压力全在自己身上', '只看身份证、工作经历，很多隐藏风险根本看不出来'],
      solutions: ['上岗前对服务人员进行多维背景核查', '一份清晰的风险提示报告', '所有审核过程可回溯'],
      colorScheme: 'green',
      size: 'medium',
    },
    {
      icon: IconUsers,
      title: 'HR / 人力资源 / 用工管理',
      problems: ['简历都很完美，谁知道有没有隐藏问题？', '要背调又没专门团队，全靠打电话问，效率低、也不专业'],
      solutions: [
        '对候选人进行身份、法律、风险信号等多维检查',
        '报告突出重点风险点，HR 不需要懂风控也看得明白',
        '特别适合关键岗位录用前把一关',
      ],
      colorScheme: 'purple',
      size: 'medium',
    },
    {
      icon: IconBriefcase,
      title: '小微企业合作 / 代理 / 企业服务',
      problems: ['收款总是拖来拖去，合作前其实谁也说不清对方情况', '不敢贸然拒绝，又害怕做错决定'],
      solutions: [
        '输入企业关键信息，一键生成信用与风险画像',
        '看清是否存在法律纠纷、执行风险等重要信号',
        '为「要不要合作、合作做到什么程度」提供参考依据',
      ],
      colorScheme: 'orange',
      size: 'large',
    },
  ]

  const colorSchemes = {
    blue: {
      bg: 'bg-gradient-to-br from-blue-50 to-white',
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
      accent: 'text-blue-600',
      border: 'border-blue-100',
    },
    green: {
      bg: 'bg-gradient-to-br from-green-50 to-white',
      iconBg: 'bg-gradient-to-br from-green-500 to-green-600',
      accent: 'text-green-600',
      border: 'border-green-100',
    },
    purple: {
      bg: 'bg-gradient-to-br from-purple-50 to-white',
      iconBg: 'bg-gradient-to-br from-purple-500 to-purple-600',
      accent: 'text-purple-600',
      border: 'border-purple-100',
    },
    orange: {
      bg: 'bg-gradient-to-br from-orange-50 to-white',
      iconBg: 'bg-gradient-to-br from-orange-500 to-orange-600',
      accent: 'text-orange-600',
      border: 'border-orange-100',
    },
  }

  return (
    <section id="scenarios" className="py-32 bg-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-orange-50/30 to-transparent blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <SectionTitle
          title="这些场景里，优查查就是你「多看一眼」的那双眼睛"
          subtitle="很多风险，其实在事前就能看出端倪。优查查把分散在各处的风险信号汇总成一份好读、好理解的报告，让门店经理、HR、小微企业老板也能轻松做判断。"
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 auto-rows-auto">
          {scenarios.map((scenario, index) => {
            const scheme = colorSchemes[scenario.colorScheme]
            const isLarge = scenario.size === 'large'

            return (
              <div key={index} className={`${isLarge ? 'lg:col-span-2' : 'lg:col-span-1'} group`}>
                <PremiumCard className={`p-8 lg:p-10 h-full ${scheme.bg} ${scheme.border} border-2`}>
                  <div className="flex items-start gap-6">
                    <div
                      className={`flex-shrink-0 w-14 h-14 rounded-2xl ${scheme.iconBg} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                    >
                      <scenario.icon size={28} className="text-white" />
                    </div>

                    <div className="flex-1 space-y-6">
                      <h3 className={`text-2xl font-bold ${scheme.accent}`}>{scenario.title}</h3>

                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-500 mb-3">日常困扰：</h4>
                          <div className="space-y-2">
                            {scenario.problems.map((problem, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <span className="text-gray-400 mt-1">·</span>
                                <p className="text-sm text-gray-600 leading-relaxed">{problem}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className={`text-sm font-semibold ${scheme.accent} mb-3`}>优查查怎么帮你：</h4>
                          <div className="space-y-2">
                            {scenario.solutions.map((solution, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <span className={scheme.accent}>✓</span>
                                <p className="text-sm text-gray-700 leading-relaxed font-medium">{solution}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isLarge && (
                    <div
                      className={`absolute bottom-0 right-0 w-32 h-32 ${scheme.iconBg} opacity-5 blur-2xl rounded-tl-full`}
                    ></div>
                  )}
                </PremiumCard>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
